// ── NY wall-clock helpers ────────────────────────────────────────────────────

// `Intl.DateTimeFormat` construction is ~10-50µs per instance — wildly
// expensive when called once per bar across thousands of bars per indicator
// recompute. These three formatters are stateless given their options, so
// reuse a single instance per format. The per-timestamp result caches below
// then dedup the *output* across repeated lookups of the same bar.t value.
const HOURS_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
  timeZone: 'America/New_York',
})
const CAL_FMT = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'America/New_York',
})

const hoursCache = new Map<number, { h: number; m: number }>()
const calCache = new Map<number, string>()
const tradingDateCache = new Map<number, string>()

/**
 * Convert epoch ms to NY wall-clock `{ h, m }`. Cached by `ms` — repeat
 * lookups for the same timestamp are free.
 */
export function toNYHours(ms: number): { h: number; m: number } {
  const cached = hoursCache.get(ms)
  if (cached) return cached
  const parts = HOURS_FMT.formatToParts(ms)
  let h = 0
  let m = 0
  for (const part of parts) {
    if (part.type === 'hour') h = parseInt(part.value, 10)
    if (part.type === 'minute') m = parseInt(part.value, 10)
  }
  const out = { h, m }
  hoursCache.set(ms, out)
  return out
}

/**
 * NY trading date key for a given epoch ms.
 *
 * Trading date rule: bars from 04:00–09:30 ET on a calendar day belong to
 * the *previous* trading date (the day the market opened). Bars from 09:30–16:00
 * ET belong to that calendar day. Bars after 16:00 ET also belong to that
 * calendar day.
 *
 * Implementation: convert to NY wall-clock, if hour < 9.5 → subtract one day
 * from the calendar date. Format: "YYYY-MM-DD".
 */
export function nyDateKey(ms: number): string {
  const cached = tradingDateCache.get(ms)
  if (cached !== undefined) return cached
  const parts = CAL_FMT.formatToParts(ms)
  let year = ''
  let month = ''
  let day = ''
  for (const part of parts) {
    if (part.type === 'year') year = part.value
    if (part.type === 'month') month = part.value
    if (part.type === 'day') day = part.value
  }
  // Parse the NY date
  const nyDate = new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)))
  // Check if premarket (before 09:30 ET)
  const nyHours = toNYHours(ms)
  if (nyHours.h < 9 || (nyHours.h === 9 && nyHours.m < 30)) {
    // Subtract one day
    nyDate.setUTCDate(nyDate.getUTCDate() - 1)
  }
  // Return ISO date string
  const y = nyDate.getUTCFullYear()
  const m = String(nyDate.getUTCMonth() + 1).padStart(2, '0')
  const d = String(nyDate.getUTCDate()).padStart(2, '0')
  const out = `${y}-${m}-${d}`
  tradingDateCache.set(ms, out)
  return out
}

/**
 * NY *calendar* date key (no trading-date adjustment).
 * Use this for "today's premarket" / "today's RTH" matching, where bars at
 * 04:30 ET on calendar day X should match RTH bars also on calendar day X.
 */
export function nyCalendarDate(ms: number): string {
  const cached = calCache.get(ms)
  if (cached !== undefined) return cached
  const parts = CAL_FMT.formatToParts(ms)
  let year = ''
  let month = ''
  let day = ''
  for (const part of parts) {
    if (part.type === 'year') year = part.value
    if (part.type === 'month') month = part.value
    if (part.type === 'day') day = part.value
  }
  const out = `${year}-${month}-${day}`
  calCache.set(ms, out)
  return out
}

// ── Session range parsing ────────────────────────────────────────────────────

/**
 * Parse a "HHMM-HHMM" string into `{ start: [h, m], end: [h, m] }`.
 * Example: "0930-0945" → `{ start: [9, 30], end: [9, 45] }`.
 */
export function parseHHMMRange(range: string): { start: [number, number]; end: [number, number] } {
  const [startStr, endStr] = range.split('-')
  const startH = Math.floor(parseInt(startStr, 10) / 100)
  const startM = parseInt(startStr, 10) % 100
  const endH = Math.floor(parseInt(endStr, 10) / 100)
  const endM = parseInt(endStr, 10) % 100
  return { start: [startH, startM], end: [endH, endM] }
}

/**
 * Check if `ms` falls within a given NY session range.
 * Both start and end are `[hour, minute]` pairs in NY wall-clock.
 */
export function isInNySession(ms: number, sessionRange: { start: [number, number]; end: [number, number] }): boolean {
  const { h, m } = toNYHours(ms)
  const currentMinutes = h * 60 + m
  const [startH, startM] = sessionRange.start
  const [endH, endM] = sessionRange.end
  const startMinutes = startH * 60 + startM
  const endMinutes = endH * 60 + endM
  return currentMinutes >= startMinutes && currentMinutes < endMinutes
}

// ── Session constants ────────────────────────────────────────────────────────

/** Regular Trading Hours: 09:30–16:00 ET */
export const RTH: { start: [number, number]; end: [number, number] } = {
  start: [9, 30],
  end: [16, 0],
}

/** Premarket session: 04:00–09:30 ET */
export const PREMARKET: { start: [number, number]; end: [number, number] } = {
  start: [4, 0],
  end: [9, 30],
}

/** Opening range: 09:30–09:45 ET */
export const OPENING_RANGE: { start: [number, number]; end: [number, number] } = {
  start: [9, 30],
  end: [9, 45],
}
